import React, { useState, useEffect } from 'react';
import { 
  FaArrowLeft, FaBuilding, FaIndustry, FaChartLine, 
  FaCalendarAlt, FaRegClock, FaRegCalendarCheck, FaLock, 
  FaRegHourglass, FaCheckCircle, FaUsers, FaSyncAlt, FaWallet, FaGlobe, FaShieldAlt, FaArrowUp
} from 'react-icons/fa';
import { 
  MdBusiness, MdAttachMoney, MdDashboard, MdTimeline, MdSpeed
} from 'react-icons/md';
import { IoFlash, IoBusiness } from 'react-icons/io5';
import BottomNavigation from './BottomNavigation';
import axios from 'axios';
import { useAuth } from '../security/security/AuthContext';

const Order = () => {
  const [activeTab, setActiveTab] = useState('normal');
  const [investments, setInvestments] = useState([]);
  const { userDetails, token, updateUserDetails } = useAuth();
  const [toastNotification, setToastNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshUserData = async () => {
    try {
      const userResponse = await axios.get(`https://api.globallequityfinance.com/api/v1/auth/${userDetails.email}`, {
        headers: {
          'Authorization': token
        }
      });
      updateUserDetails(userResponse.data);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setToastNotification({ message: 'Failed to refresh user data', type: 'error' });
    }
  };

  const refreshAllData = async () => {
    setIsRefreshing(true);
    setIsLoading(true);
    try {
      await Promise.all([
        refreshUserData(),
        fetchInvestments()
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setIsRefreshing(false);
      }, 800);
    }
  };

  useEffect(() => {
    refreshAllData();
    // Set up auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      refreshAllData();
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`https://api.globallequityfinance.com/api/investments/user/${userDetails.email}`);
      // Sort investments by ID in descending order (higher IDs first)
      const sortedInvestments = response.data.sort((a, b) => b.id - a.id);
      setInvestments(sortedInvestments);
    } catch (error) {
      console.error('Error fetching investments:', error);
      setToastNotification({ 
        message: 'An error occurred while fetching portfolio data. Please try again.', 
        type: 'error' 
      });
    }
  };

  const handleReceive = async (investmentId, productType) => {
    try {
      const investment = investments.find(inv => inv.id === investmentId);
      const isHighValueProduct = investment.investedAmount >= 5000;
      
      if (isHighValueProduct && new Date(investment.validUntil) > new Date()) {
        setToastNotification({ 
          message: 'This investment can only be collected at the end of its cycle.', 
          type: 'error' 
        });
        return;
      }

      await axios.post(`https://api.globallequityfinance.com/api/investments/${investmentId}/receive`);
      await refreshAllData();
      setToastNotification({ 
        message: 'Returns collected successfully!', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error receiving income:', error);
      let errorMessage = 'An error occurred while collecting returns. Please try again.';
      
      if (error.response && error.response.data) {
        errorMessage = error.response.data;
      }
      
      setToastNotification({ 
        message: errorMessage, 
        type: 'error' 
      });
    }
  };

  // Helper to get icon based on company name
  const getCompanyIcon = (productName) => {
    const name = productName.toLowerCase();
    if (name.includes('tech') || name.includes('digital')) return <MdDashboard />;
    if (name.includes('bank') || name.includes('finance')) return <MdAttachMoney />;
    if (name.includes('energy') || name.includes('oil')) return <FaIndustry />;
    if (name.includes('real estate') || name.includes('property')) return <FaBuilding />;
    if (name.includes('retail') || name.includes('consumer')) return <FaUsers />;
    if (name.includes('pharma') || name.includes('health')) return <FaShieldAlt />;
    return <IoBusiness />;
  };

  // Format date to relative time (e.g., "3 days left" or "Ended 2 days ago")
  const getTimeRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return { text: `${diffDays} day${diffDays !== 1 ? 's' : ''} left`, icon: <FaRegHourglass /> };
    } else if (diffDays === 0) {
      return { text: 'Ends today', icon: <FaRegCalendarCheck /> };
    } else {
      return { text: `Ended ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`, icon: <FaRegClock /> };
    }
  };

  // Calculate progress percentage of investment contract
  const calculateProgress = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    
    const totalDuration = end - start;
    const elapsed = now - start;
    
    if (elapsed <= 0) return 0;
    if (elapsed >= totalDuration) return 100;
    
    return Math.min(100, Math.floor((elapsed / totalDuration) * 100));
  };

  // Get color for company sector
  const getCompanyColor = (productName) => {
    const name = productName.toLowerCase();
    if (name.includes('tech') || name.includes('digital')) return '#2563EB';
    if (name.includes('bank') || name.includes('finance')) return '#059669';
    if (name.includes('energy') || name.includes('oil')) return '#DC2626';
    if (name.includes('real estate') || name.includes('property')) return '#7C3AED';
    if (name.includes('retail') || name.includes('consumer')) return '#EA580C';
    if (name.includes('pharma') || name.includes('health')) return '#0891B2';
    return '#e74c3c';
  };

  const formatKSH = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Add this to your component logic
  const handleCloseToast = () => {
    setToastNotification(null);
  };

  // Auto-dismiss functionality
  useEffect(() => {
    if (toastNotification) {
      const timer = setTimeout(() => {
        handleCloseToast();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [toastNotification]);

  return (
    <div className="ge-portfolio-dashboard">
      <header className="ge-portfolio-header">
        <div className="ge-header-content">
          <h1 className="ge-portfolio-title">
            <FaChartLine className="ge-title-icon" /> Portfolio
          </h1>
          <button className={`ge-refresh-button ${isRefreshing ? 'rotating' : ''}`} onClick={refreshAllData}>
            <FaSyncAlt />
          </button>
        </div>
      </header>
      
      <nav className="ge-portfolio-tabs">
        <button 
          className={`ge-tab-button ${activeTab === 'normal' ? 'ge-active' : ''}`}
          onClick={() => setActiveTab('normal')}
        >
          <FaArrowUp className="ge-tab-icon" />
          <span className="ge-tab-text">Active Investments</span>
        </button>
        <button 
          className={`ge-tab-button ${activeTab === 'expired' ? 'ge-active' : ''}`}
          onClick={() => setActiveTab('expired')}
        >
          <FaCheckCircle className="ge-tab-icon" />
          <span className="ge-tab-text">Completed</span>
        </button>
      </nav>
      
      <div className="ge-portfolio-list">
        {isLoading ? (
          <div className="ge-loading-container">
            <div className="ge-loading-spinner"></div>
            <p className="ge-loading-text">Loading portfolio...</p>
          </div>
        ) : (
          <>
            {investments
              .filter(inv => activeTab === 'normal' ? inv.active : !inv.active)
              .map(investment => {
                const isHighValueProduct = investment.investedAmount >= 5000;
                const isReadyToReceive = !isHighValueProduct || 
                  (isHighValueProduct && new Date(investment.validUntil) <= new Date());
                
                const timeInfo = getTimeRemaining(investment.validUntil);
                const progress = calculateProgress(investment.createdAt, investment.validUntil);
                const companyColor = getCompanyColor(investment.productName);
                
                return (
                  <div key={investment.id} className="ge-investment-card">
                    <div className="ge-card-header">
                      <div className="ge-company-info">
                        <div className="ge-company-icon" style={{ 
                          backgroundColor: `${companyColor}20`, 
                          color: companyColor 
                        }}>
                          {getCompanyIcon(investment.productName)}
                        </div>
                        <div className="ge-company-details">
                          <h3 className="ge-company-name">{investment.productName}</h3>
                          <span className={`ge-status-badge ${investment.active ? 'ge-active' : 'ge-completed'}`}>
                            {investment.active ? 'Active' : 'Completed'}
                          </span>
                        </div>
                      </div>
                      <div className="ge-returns-display">
                        <span className="ge-returns-label">Returns</span>
                        <span className="ge-returns-amount" style={{ color: companyColor }}>
                          {formatKSH(investment.earnedAmount)}
                        </span>
                      </div>
                    </div>
                    
                    {investment.active && (
                      <div className="ge-progress-section">
                        <div className="ge-progress-bar">
                          <div 
                            className="ge-progress-fill" 
                            style={{ 
                              width: `${progress}%`,
                              backgroundColor: companyColor
                            }}
                          ></div>
                        </div>
                        <div className="ge-progress-info">
                          <span className="ge-progress-text">{progress}% Complete</span>
                          <span className="ge-time-remaining">
                            {timeInfo.icon} {timeInfo.text}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <div className="ge-investment-metrics">
                      <div className="ge-metric">
                        <FaWallet className="ge-metric-icon" />
                        <div className="ge-metric-content">
                          <span className="ge-metric-label">Investment</span>
                          <span className="ge-metric-value">{formatKSH(investment.investedAmount)}</span>
                        </div>
                      </div>
                      
                      <div className="ge-metric">
                        <IoFlash className="ge-metric-icon" />
                        <div className="ge-metric-content">
                          <span className="ge-metric-label">Daily Return</span>
                          <span className="ge-metric-value">{formatKSH(investment.dailyIncome)}</span>
                        </div>
                      </div>
                      
                      <div className="ge-metric">
                        <FaChartLine className="ge-metric-icon" />
                        <div className="ge-metric-content">
                          <span className="ge-metric-label">Total Return</span>
                          <span className="ge-metric-value">{formatKSH(investment.totalIncome)}</span>
                        </div>
                      </div>
                      
                      <div className="ge-metric">
                        <FaCalendarAlt className="ge-metric-icon" />
                        <div className="ge-metric-content">
                          <span className="ge-metric-label">End Date</span>
                          <span className="ge-metric-value">{new Date(investment.validUntil).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    {investment.active && (
                      <div className="ge-card-footer">
                        <button 
                          className={`ge-collect-button ${(!isReadyToReceive || investment.earnedAmount <= 0) ? 'ge-disabled' : ''}`}
                          onClick={() => handleReceive(investment.id, investment.productType)}
                          disabled={!isReadyToReceive || investment.earnedAmount <= 0}
                          style={{ 
                            ...(isReadyToReceive && investment.earnedAmount > 0 ? {
                              backgroundColor: companyColor,
                              borderColor: companyColor
                            } : {})
                          }}
                        >
                          {!isReadyToReceive ? (
                            <>
                              <FaLock className="ge-button-icon" />
                              <span>Locked Until Maturity</span>
                            </>
                          ) : (
                            investment.earnedAmount <= 0 ? 'No Returns Available' : 'Collect Returns'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              
            {investments.filter(inv => activeTab === 'normal' ? inv.active : !inv.active).length === 0 && (
              <div className="ge-empty-state">
                <div className="ge-empty-icon">
                  {activeTab === 'normal' ? <FaArrowUp  /> : <FaCheckCircle />}
                </div>
                <h3 className="ge-empty-title">
                  {activeTab === 'normal' 
                    ? "No Active Investments" 
                    : "No Completed Investments"}
                </h3>
                <p className="ge-empty-text">
                  {activeTab === 'normal' 
                    ? "Start building your portfolio by investing in company shares." 
                    : "Your completed investments will appear here."}
                </p>
                {activeTab === 'normal' && (
                  <button 
                    className="ge-invest-button"
                    onClick={() => window.location.href = '/shares'}
                  >
                    <FaGlobe />
                    Explore Companies
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      <BottomNavigation />
      
      {toastNotification && (
        <div className={`ge-toast ${toastNotification.type === 'success' ? 'ge-success' : 'ge-error'}`}>
          {toastNotification.type === 'success' ? (
            <FaCheckCircle className="ge-toast-icon" />
          ) : (
            <FaArrowLeft className="ge-toast-icon" />
          )}
          <div className="ge-toast-message">{toastNotification.message}</div>
        </div>
      )}
      
      <style jsx>{`
        .ge-portfolio-dashboard {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          color: #F0F0F0;
          min-height: 100vh;
          padding-bottom: 100px;
          font-family: 'Inter', system-ui, sans-serif;
        }
        
        .ge-portfolio-header {
          background: linear-gradient(135deg, rgba(23, 42, 69, 0.9), rgba(16, 20, 31, 0.95));
          padding: 24px 16px;
          border-bottom: 1px solid rgba(231, 76, 60, 0.2);
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }
        
        .ge-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .ge-portfolio-title {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 12px;
          color: #FFFFFF;
        }
        
        .ge-title-icon {
          color: #e74c3c;
          font-size: 26px;
        }
        
        .ge-refresh-button {
          background: rgba(231, 76, 60, 0.1);
          border: 1px solid rgba(231, 76, 60, 0.3);
          color: #e74c3c;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .ge-refresh-button:hover {
          background: rgba(231, 76, 60, 0.2);
          transform: translateY(-2px);
        }
        
        .ge-refresh-button.rotating {
          animation: ge-rotate 1s linear infinite;
        }
        
        @keyframes ge-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .ge-portfolio-tabs {
          display: flex;
          padding: 0 16px;
          max-width: 600px;
          margin: 20px auto 0;
          gap: 12px;
        }
        
        .ge-tab-button {
          flex: 1;
          background: rgba(23, 42, 69, 0.6);
          border: 1px solid rgba(231, 76, 60, 0.2);
          border-radius: 12px;
          padding: 14px;
          font-size: 14px;
          color: #8892b0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
        }
        
        .ge-tab-button.ge-active {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          color: white;
          border-color: #e74c3c;
          box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
        }
        
        .ge-tab-button:hover:not(.ge-active) {
          background: rgba(231, 76, 60, 0.1);
          color: #e74c3c;
        }
        
        .ge-tab-icon {
          font-size: 16px;
        }
        
        .ge-tab-text {
          font-weight: 600;
        }
        
        .ge-portfolio-list {
          padding: 20px 16px 0;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .ge-investment-card {
          background: rgba(23, 42, 69, 0.9);
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 16px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(231, 76, 60, 0.2);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .ge-investment-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
        }
        
        .ge-card-header {
          padding: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(231, 76, 60, 0.1);
        }
        
        .ge-company-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .ge-company-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        
        .ge-company-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .ge-company-name {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #FFFFFF;
        }
        
        .ge-status-badge {
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 6px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .ge-status-badge.ge-active {
          background: rgba(16, 185, 129, 0.1);
          color: #10B981;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }
        
        .ge-status-badge.ge-completed {
          background: rgba(239, 68, 68, 0.1);
          color: #EF4444;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        
        .ge-returns-display {
          text-align: right;
        }
        
        .ge-returns-label {
          display: block;
          font-size: 12px;
          color: #8892b0;
          margin-bottom: 4px;
        }
        
        .ge-returns-amount {
          font-size: 18px;
          font-weight: 700;
          display: block;
        }
        
        .ge-progress-section {
          padding: 16px 20px;
          background: rgba(16, 20, 31, 0.4);
        }
        
        .ge-progress-bar {
          height: 6px;
          background: rgba(136, 146, 176, 0.2);
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 10px;
        }
        
        .ge-progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s ease;
        }
        
        .ge-progress-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
        }
        
        .ge-progress-text {
          color: #8892b0;
          font-weight: 500;
        }
        
        .ge-time-remaining {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #10B981;
          font-weight: 600;
        }
        
        .ge-investment-metrics {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          padding: 20px;
          background: rgba(16, 20, 31, 0.2);
        }
        
        .ge-metric {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .ge-metric-icon {
          width: 32px;
          height: 32px;
          background: rgba(231, 76, 60, 0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #e74c3c;
          font-size: 14px;
          flex-shrink: 0;
        }
        
        .ge-metric-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .ge-metric-label {
          font-size: 11px;
          color: #8892b0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .ge-metric-value {
          font-size: 13px;
          font-weight: 600;
          color: #FFFFFF;
        }
        
        .ge-card-footer {
          padding: 20px;
          border-top: 1px solid rgba(231, 76, 60, 0.1);
        }
        
        .ge-collect-button {
          width: 100%;
          background: rgba(136, 146, 176, 0.2);
          color: #8892b0;
          border: 1px solid rgba(136, 146, 176, 0.3);
          border-radius: 12px;
          padding: 14px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
        }
        
        .ge-collect-button:hover:not(.ge-disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        }
        
        .ge-collect-button.ge-disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .ge-button-icon {
          font-size: 16px;
        }
        
        .ge-empty-state {
          padding: 60px 20px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          background: rgba(23, 42, 69, 0.6);
          border-radius: 16px;
          margin-top: 20px;
        }
        
        .ge-empty-icon {
          font-size: 56px;
          color: #8892b0;
          margin-bottom: 20px;
          opacity: 0.7;
        }
        
        .ge-empty-title {
          font-size: 20px;
          font-weight: 600;
          color: #FFFFFF;
          margin: 0 0 12px 0;
        }
        
        .ge-empty-text {
          color: #8892b0;
          font-size: 15px;
          margin: 0 0 24px 0;
          line-height: 1.5;
        }
        
        .ge-invest-button {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 14px 24px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
        }
        
        .ge-invest-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(231, 76, 60, 0.4);
        }
        
        .ge-loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 0;
        }
        
        .ge-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(231, 76, 60, 0.2);
          border-radius: 50%;
          border-top-color: #e74c3c;
          animation: ge-spin 1s linear infinite;
          margin-bottom: 16px;
        }
        
        @keyframes ge-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .ge-loading-text {
          color: #8892b0;
          font-size: 16px;
        }
        
        .ge-toast {
          position: fixed;
          bottom: 100px;
          left: 50%;
          transform: translateX(-50%);
          width: 90%;
          max-width: 400px;
          padding: 16px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          animation: ge-slideUp 0.3s ease forwards;
          z-index: 1000;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(8px);
        }
        
        .ge-toast.ge-success {
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.3);
        }
        
        .ge-toast.ge-error {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
        }
        
        .ge-toast-icon {
          font-size: 20px;
        }
        
        .ge-success .ge-toast-icon{
         color: #10B981;
       }
       
       .ge-error .ge-toast-icon {
         color: #EF4444;
       }
       
       .ge-toast-message {
         flex: 1;
         color: #F8FAFC;
         font-size: 14px;
         font-weight: 500;
       }
       
       @keyframes ge-slideUp {
         from {
           opacity: 0;
           transform: translate(-50%, 20px);
         }
         to {
           opacity: 1;
           transform: translate(-50%, 0);
         }
       }
       
       /* Media queries for responsive design */
       @media (max-width: 480px) {
         .ge-portfolio-header {
           padding: 20px 12px;
         }
         
         .ge-portfolio-title {
           font-size: 20px;
         }
         
         .ge-title-icon {
           font-size: 22px;
         }
         
         .ge-portfolio-tabs {
           padding: 0 12px;
           gap: 8px;
         }
         
         .ge-tab-button {
           padding: 12px;
           font-size: 13px;
         }
         
         .ge-tab-text {
           display: none;
         }
         
         .ge-portfolio-list {
           padding: 16px 12px 0;
         }
         
         .ge-card-header {
           padding: 16px;
         }
         
         .ge-company-icon {
           width: 40px;
           height: 40px;
           font-size: 18px;
         }
         
         .ge-company-name {
           font-size: 14px;
         }
         
         .ge-returns-amount {
           font-size: 16px;
         }
         
         .ge-investment-metrics {
           padding: 16px;
           gap: 12px;
         }
         
         .ge-metric-icon {
           width: 28px;
           height: 28px;
           font-size: 12px;
         }
         
         .ge-metric-value {
           font-size: 12px;
         }
         
         .ge-card-footer {
           padding: 16px;
         }
         
         .ge-collect-button {
           padding: 12px;
           font-size: 13px;
         }
         
         .ge-empty-state {
           padding: 40px 16px;
         }
         
         .ge-empty-icon {
           font-size: 48px;
         }
         
         .ge-empty-title {
           font-size: 18px;
         }
         
         .ge-empty-text {
           font-size: 14px;
         }
       }
       
       @media (max-width: 360px) {
         .ge-investment-metrics {
           grid-template-columns: 1fr;
           gap: 10px;
         }
         
         .ge-card-header {
           flex-direction: column;
           align-items: flex-start;
           gap: 12px;
         }
         
         .ge-returns-display {
           text-align: left;
           align-self: flex-end;
         }
       }
     `}</style>
   </div>
 );
};

export default Order;